import axios, { AxiosRequestConfig, AxiosInstance } from 'axios';
import { ElMessageBox } from 'element-plus';
import useStore from '@/store';
import { getToken } from '@/utils/auth';
import { RESPONSE_STATUS_CODE } from '@/constant/system/enums';
import { toast } from './message';

type Response<T = any> = Promise<{
  code: string
  data: T
  message: string
}>

interface MessageConfig {
  showErrorMessage?: boolean;
  showSuccessMessage?: boolean;
  errorMessage?: string;
  successMessage?: string
}

function sRequest(axiosConfig: AxiosRequestConfig, messageConfig?: MessageConfig): Response {
  const request = new _AxiosRequest(messageConfig)
  return request.send(axiosConfig) as any
}

class _AxiosRequest {
  private showErrorMessage = true
  private showSuccessMessage = false
  private errorMessage = ''
  private successMessage = ''
  private axiosService!: AxiosInstance
  
  constructor(messageConfig?: MessageConfig) {
    if (messageConfig) {
      this.showErrorMessage = messageConfig.showErrorMessage ?? true;
      this.showSuccessMessage = messageConfig.showSuccessMessage ?? false;
      this.errorMessage = messageConfig.errorMessage ?? '';
      this.successMessage = messageConfig.successMessage ?? '请求成功！'
    }
    this.init()
  }

  public send(axiosConfig: AxiosRequestConfig) {
    return this.axiosService(axiosConfig)
  }

  handleUnauthorized() {
    ElMessageBox.confirm('登录状态已过期, 请重新登录。', '状态过期', {
      confirmButtonText: '重新登录',
      cancelButtonText: '取消',
      type: 'warning'
    })
  }

  handleNoPermisson() {
    toast.error('请求失败,接口无权限')
  }
  
  handleNotFound() {
    toast.error('请求失败，未找到请求的资源')
  }

  init() {
    this.axiosService = axios.create({
      baseURL: process.env.VUE_APP_BASE_API,
      timeout: 30000,
      headers: { 'Content-Type': 'application/json;charset=utf-8' }
    });
    
    // 请求拦截器
    this.axiosService.interceptors.request.use(
      config => {
        if (!config.headers) {
          throw new Error(
            `Expected 'config' and 'config.headers' not to be undefined`
          );
        }
        const { user } = useStore();
        if (user.token) {
          config.headers.Authorization = `${getToken()}`;
        }
        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );
    
    // 响应拦截器
    this.axiosService.interceptors.response.use(
      response => {
    
        if (response.status === 401) {
          this.handleUnauthorized()
          return Promise.reject(response)
        }
        if (response.status === 403 && this.showErrorMessage) {
          this.errorMessage ? toast.error(this.errorMessage) : this.handleNoPermisson()
          return Promise.reject(response)
        }
    
        const res = response.data
        const contentType = response.headers['content-type']

        if (response.status === 200 && res.code === RESPONSE_STATUS_CODE.SUCCESS && this.showSuccessMessage) {
          toast.success(this.successMessage)
        }
    
        if ((res instanceof Blob) || (!contentType || contentType.indexOf('application/json') === -1) || !res.code) {
          return response
        }
    
        if (res.code === RESPONSE_STATUS_CODE.UNAUTHORIZED) {
          this.handleUnauthorized()
          return Promise.reject(res)
        } else if (res.code !== RESPONSE_STATUS_CODE.SUCCESS) {
          // response.status !== 200
          if (this.showErrorMessage) {
            toast.error(this.errorMessage || res.message || '请求失败')
          }
          return Promise.reject(res)
        } else {
          return res
        }
      },
      error => {
        let message = this.errorMessage || '请求出错，请尝试重新发起请求或联系系统管理员'
        if (error.response) {
          if (error.response.status === 401) {
            this.handleUnauthorized()
          } else if (this.showErrorMessage && this.errorMessage) {
            toast.error(message)
          } else if (this.showErrorMessage && error.response.status === 404) {
            this.handleNotFound()
          } else if (this.showErrorMessage && error.response.status === 403) {
            this.handleNoPermisson()
          } else if(this.showErrorMessage) {
            toast.error(error.response?.data?.message || message)
          }
        } else {
          if (error.message.indexOf('timeout') !== -1) {
            message = this.errorMessage || '请求超时，请尝试重新发起请求或联系系统管理员'
          }
          if (this.showErrorMessage) {
            toast.error(message)
          }
        }
        return Promise.reject(error)
      }
    );
  }
}

// 导出 axios 实例
export default sRequest;
